// components/CustomScriptInjector/collect.ts — hooks.ts split (sub-module collect scripts)
import type { Form } from '../../types';
import { normalizeGoogleScript } from './google';

/**
 * Kumpulkan konten script dari 2 mode:
 * 1. scriptContent langsung (LandingPageViewer)
 * 2. customScripts per platform per lokasi (FormViewerPage) + legacy
 *
 * dedupeKey (2026-10-22): konversi Ads `lead` di-inject RAW tanpa eventID, jadi
 * reload halaman thanks mengirim konversi GANDA. Sekali per order/sesi — key
 * pakai orderId dari pemanggil (fallback formId). Snippet `tag` TETAP selalu
 * di-inject; yang di-skip hanya event `lead` yang sudah pernah terkirim.
 */
export const collectScripts = (form?: Form, location?: 'formPage' | 'thankYouPage', scriptContent?: string, dedupeKey?: string): string[] => {
    const scriptsToInjectList: string[] = [];

    // Mode 1: Direct scriptContent (for LandingPageViewer)
    if (scriptContent?.trim()) {
        scriptsToInjectList.push(scriptContent);
    }
    // Mode 2: Form-based scripts (for FormViewerPage)
    else if (form && location) {
        // Collect platform-specific scripts
        if (form.customScripts?.platforms) {
            const { google, tiktok, meta, snack, other } = form.customScripts.platforms;

            // Google — 2 mode (backward compatible):
            //  baru: tag (base, 2 halaman) + vc (formPage) + lead (thankYouPage);
            //        atc/ic SENGAJA tidak di-inject (di-fire saat interaksi, lihat
            //        utils/googleAdsConversion.ts + effectsFunnel/actionsSubmit).
            //  legacy: formPage/thankYouPage (blob) tetap di-inject apa adanya.
            if (google) {
                const g = google as Record<string, string | undefined>;
                const pushG = (s?: string) => { if (s?.trim()) scriptsToInjectList.push(normalizeGoogleScript(s)); };
                pushG(g.tag);
                if (location === 'formPage') { pushG(g.vc); pushG(g.formPage); }
                if (location === 'thankYouPage') {
                    // Dedupe konversi Ads Lead (sekali per order/sesi).
                    const key = `ga_ads_lead_${dedupeKey || form.id || 'form'}`;
                    let allowLead = true;
                    try { allowLead = !sessionStorage.getItem(key); } catch { allowLead = true; }
                    if (allowLead) {
                        pushG(g.lead);
                        try { sessionStorage.setItem(key, '1'); } catch { /* storage ditolak */ }
                    }
                    pushG(g.thankYouPage);
                }
            }
            if (tiktok?.[location]?.trim()) scriptsToInjectList.push(tiktok[location]!);
            if (meta?.[location]?.trim()) scriptsToInjectList.push(meta[location]!);
            if (snack?.[location]?.trim()) scriptsToInjectList.push(snack[location]!);
            if (other?.[location]?.trim()) scriptsToInjectList.push(other[location]!);
        }

        // Also include legacy scripts for backward compatibility
        if (location === 'formPage' && form.customScripts?.formPage?.trim()) {
            scriptsToInjectList.push(form.customScripts.formPage);
        }
        if (location === 'thankYouPage' && form.customScripts?.thankYouPage?.trim()) {
            scriptsToInjectList.push(form.customScripts.thankYouPage);
        }
    }

    return scriptsToInjectList;
};
